/**
 * RESSO Scoring Library
 *
 * Two formulas live here (mirroring SchwagerDigital's split):
 *
 * 1. Server-side (source of truth, used in apps/api/src/modules/resso/scoring.service.ts):
 *    final = documental * 0.4 + terreno * 0.3 + workerCompliance * 0.3
 *    Matches Schwager backend ressoScoring.service.ts (WEIGHT_* constants).
 *    This is what Codelco threshold gating reads.
 *
 * 2. Client-side preview (display-only, optimistic UI on AuditoriaContratoTab):
 *    final = (documental * 0.5 + terreno * 0.5) - hallazgosDiscount + bonus
 *    Ported from SchwagerDigital frontend/src/utils/ressoScoring.ts.
 *    Surfaces the audit form's Hallazgos N1/N2 + bonus rules so the auditor
 *    sees in real time what their entries imply. NOT persisted as scoreFinal —
 *    the canonical scoreFinal stays the server formula above.
 *
 * Codelco audit thresholds:
 *   ACEPTABLE    >= 85
 *   MODERADO     >= 80 and < 85
 *   INACEPTABLE  < 80   (contract termination risk)
 *
 * The classify() function MUST match server and client to keep UI consistent.
 */

export const THRESHOLD_ACEPTABLE = 85;
export const THRESHOLD_CRITICO = 80;

export type RessoClasificacionKey = 'aceptable' | 'moderado' | 'inaceptable';

export interface RessoScoreBreakdown {
  documental: number;
  terreno: number;
  workerCompliance: number;
}

export interface RessoFinalScore {
  scoreFinal: number;
  scoreDocumental: number;
  scoreTerreno: number;
  scoreWorkerCompliance: number;
  clasificacion: RessoClasificacionKey;
  breakdown: RessoScoreBreakdown;
}

// =============================================================================
// Hallazgos (client-side preview, ported 1:1 from Schwager frontend)
// =============================================================================

export interface HallazgosCatConfig {
  categoria: string;
  descripcion?: string;
  descuento: number;
}

export interface HallazgosTierEntry {
  cantidad: number;
  descuento: number;
}

export interface HallazgosConfig {
  categorias: HallazgosCatConfig[];
  tiers?: {
    nivel1: HallazgosTierEntry[];
    nivel2: HallazgosTierEntry[];
  };
}

export interface HallazgosData {
  /** Count of N1 hallazgos per category name */
  n1Counts: Record<string, number>;
  /** Count of N2 hallazgos per category name */
  n2Counts: Record<string, number>;
  /** 8 free-text description slots for N1 hallazgos */
  n1Textos: string[];
  /** 8 free-text description slots for N2 hallazgos */
  n2Textos: string[];
}

export const EMPTY_HALLAZGOS_DATA: HallazgosData = {
  n1Counts: {},
  n2Counts: {},
  n1Textos: ['', '', '', '', '', '', '', ''],
  n2Textos: ['', '', '', '', '', '', '', ''],
};

/** Subset of categorias that participates in the N2 group of the hallazgos table. */
export const HALLAZGOS_N2_CAT_NAMES = ['Terreno', 'Conductas', 'Liderazgo', 'Aprendizaje'];

/**
 * Returns the discount fraction for the highest matching tier given a total count.
 * Tiers are sorted descending; first tier whose cantidad <= totalCount wins. 0 otherwise.
 */
function calcTierDiscount(totalCount: number, tiers: HallazgosTierEntry[]): number {
  const sorted = [...tiers].sort((a, b) => b.cantidad - a.cantidad);
  for (const tier of sorted) {
    if (totalCount >= tier.cantidad) return tier.descuento;
  }
  return 0;
}

/**
 * Calculate the total hallazgos penalty as a fraction (0..1+).
 *
 * Two accumulating components:
 *   1. Per-category discount — `category.descuento × count` for every N1 + N2 entry.
 *   2. Tier discount — additional penalty based on TOTAL count per nivel (single step).
 *
 * The caller is responsible for clamping the final score; this returns the raw discount.
 */
export function calcHallazgosDiscount(data: HallazgosData, config: HallazgosConfig): number {
  let discount = 0;

  for (const cat of config.categorias) {
    discount += cat.descuento * (data.n1Counts[cat.categoria] ?? 0);
  }

  for (const cat of config.categorias.filter((c) => HALLAZGOS_N2_CAT_NAMES.includes(c.categoria))) {
    discount += cat.descuento * (data.n2Counts[cat.categoria] ?? 0);
  }

  if (config.tiers) {
    const totalN1 = Object.values(data.n1Counts).reduce((s, n) => s + n, 0);
    const totalN2 = Object.values(data.n2Counts).reduce((s, n) => s + n, 0);
    discount += calcTierDiscount(totalN1, config.tiers.nivel1);
    discount += calcTierDiscount(totalN2, config.tiers.nivel2);
  }

  return discount;
}

// =============================================================================
// Bonus (client-side preview, ported 1:1 from Schwager frontend)
// =============================================================================

export interface BonusRule {
  triggerQuestionId: string;
  requiredValue: number;
  nullBehavior?: string;
  bonus: number;
  description?: string;
}

/**
 * Bonus fraction (0..1). Fires when the documental response for triggerQuestionId
 * strictly equals requiredValue. null (N/A) and undefined (unanswered) do NOT trigger.
 */
export function calcBonus(
  documentalResponses: Record<string, number | null>,
  bonusRules: BonusRule[],
): number {
  let bonus = 0;
  for (const rule of bonusRules) {
    const val = documentalResponses[rule.triggerQuestionId];
    if (typeof val === 'number' && val === rule.requiredValue) {
      bonus += rule.bonus;
    }
  }
  return bonus;
}

// =============================================================================
// Client-side preview final (the 50/50 - hallazgos + bonus formula)
// =============================================================================

export interface RessoClientPreviewScore {
  scoreDocumental: number;
  scoreTerreno: number;
  hallazgosDiscount: number;
  bonus: number;
  scoreFinalPreview: number;
  clasificacion: RessoClasificacionKey;
}

/**
 * Optimistic-UI preview of the final score using Schwager's auditor-facing formula.
 * NOT persisted server-side; used only to render real-time deltas in the form.
 *
 *   preview = clamp(documental*0.5 + terreno*0.5 - hallazgosDiscount*100 + bonus*100, [0,100])
 */
export function computeClientPreviewScore(input: {
  scoreDocumental: number;
  scoreTerreno: number;
  hallazgosDiscount?: number;
  bonus?: number;
}): RessoClientPreviewScore {
  const documental = clamp(input.scoreDocumental);
  const terreno = clamp(input.scoreTerreno);
  const hallazgosDiscount = input.hallazgosDiscount ?? 0;
  const bonus = input.bonus ?? 0;
  const raw = documental * 0.5 + terreno * 0.5 - hallazgosDiscount * 100 + bonus * 100;
  const scoreFinalPreview = round2(Math.min(100, Math.max(0, raw)));
  return {
    scoreDocumental: documental,
    scoreTerreno: terreno,
    hallazgosDiscount,
    bonus,
    scoreFinalPreview,
    clasificacion: classify(scoreFinalPreview),
  };
}

/**
 * Server-side scoring formula (source of truth).
 * Inputs are pre-computed component scores 0-100.
 */
export function computeServerFinalScore(input: {
  scoreDocumental: number;
  scoreTerreno: number;
  scoreWorkerCompliance: number;
}): RessoFinalScore {
  const documental = clamp(input.scoreDocumental);
  const terreno = clamp(input.scoreTerreno);
  const workerCompliance = clamp(input.scoreWorkerCompliance);

  const scoreFinal = round2(
    documental * 0.4 + terreno * 0.3 + workerCompliance * 0.3,
  );

  return {
    scoreFinal,
    scoreDocumental: documental,
    scoreTerreno: terreno,
    scoreWorkerCompliance: workerCompliance,
    clasificacion: classify(scoreFinal),
    breakdown: { documental, terreno, workerCompliance },
  };
}

/**
 * Worker compliance rate: ratio of workers with all required documents
 * VIGENTE (not VENCIDO/RECHAZADO) to total workers.
 */
export function computeWorkerCompliance(input: {
  totalWorkers: number;
  compliantWorkers: number;
}): number {
  if (input.totalWorkers === 0) {
    return 0;
  }
  return round2((input.compliantWorkers / input.totalWorkers) * 100);
}

/**
 * Codelco classification given a final score 0-100.
 */
export function classify(scoreFinal: number): RessoClasificacionKey {
  if (scoreFinal >= THRESHOLD_ACEPTABLE) {
    return 'aceptable';
  }
  if (scoreFinal >= THRESHOLD_CRITICO) {
    return 'moderado';
  }
  return 'inaceptable';
}

/**
 * UI helper: human-readable threshold label.
 */
export function getThresholdLabel(score: number): string {
  if (score >= THRESHOLD_ACEPTABLE) {
    return 'ACEPTABLE';
  }
  if (score >= THRESHOLD_CRITICO) {
    return 'EN RIESGO';
  }
  return 'CRITICO';
}

/**
 * BLOCKER B4 fix: refuse to silently coerce NaN/Infinity to 0.
 *
 * A NaN here means upstream arithmetic was corrupt (division by zero, missing config,
 * malformed JSON). Returning 0 silently produces a false INACEPTABLE — Codelco contract
 * termination indistinguishable from a legitimate audit failure. Throw loud instead.
 */
function clamp(value: number): number {
  if (!Number.isFinite(value)) {
    throw new RessoScoringError(
      `RESSO score component is not finite (got ${String(value)}); upstream arithmetic is corrupt`,
    );
  }
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

export class RessoScoringError extends Error {
  readonly code = 'RESSO_SCORING_ERROR';
  readonly statusCode = 500;
  constructor(message: string) {
    super(message);
    this.name = 'RessoScoringError';
  }
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
